// Key-value storage of the platform module (CONTRACTS 9.5). Web Storage throws in private modes, with blocked site data
// or a full quota; every access here is wrapped, so an unavailable area simply behaves as an empty one.

export interface PlatformStorage {
  get(key: string): string | null;
  set(key: string, v: string): void;
  remove(key: string): void;
}

/** Wraps a Web Storage area, resolved on every access (reading `localStorage` itself may throw). */
export function webStorage(area: () => Storage | null | undefined): PlatformStorage {
  return {
    get(key) {
      try {
        return area()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key, v) {
      try {
        area()?.setItem(key, v);
      } catch {
        // Quota or disabled storage: the value lives only for this page.
      }
    },
    remove(key) {
      try {
        area()?.removeItem(key);
      } catch {
        // Nothing to remove when the area is unavailable.
      }
    },
  };
}

/** In-memory storage for tests and for hosts without Web Storage. */
export function memoryStorage(initial: Readonly<Record<string, string>> = {}): PlatformStorage {
  const values = new Map<string, string>(Object.entries(initial));
  return {
    get: (key) => values.get(key) ?? null,
    set: (key, v) => {
      values.set(key, v);
    },
    remove: (key) => {
      values.delete(key);
    },
  };
}

/** Reads a JSON value, or null when it is missing or unreadable. */
export function readJson(storage: PlatformStorage, key: string): unknown {
  const raw = storage.get(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export function writeJson(storage: PlatformStorage, key: string, value: unknown): void {
  storage.set(key, JSON.stringify(value));
}
